export const EXTERNAL_PLAN_INPUT_MODE = 'external_anonymized_dossier_v1';
import { formatActiveTreatments } from './clinical/currentTreatments';

export const EXTERNAL_DOSSIER_PROMPT_FILENAME = 'pack-synthese-dossier-pilote.txt';

const DIRECT_IDENTIFIER_KEYS = new Set([
  'prenom', 'nom', 'email', 'telephone', 'phone', 'adresse', 'dateNaissance',
  'date_naissance', 'medecinTraitant', 'lieu', 'profession',
]);

const PROFILE_SECTIONS = Object.freeze([
  Object.freeze({
    title: 'Objectifs et contexte',
    fields: Object.freeze([
      ['objectif_primaire', 'Objectif principal'],
      ['objectifs', 'Objectifs'],
      ['objectif_secondaire_1', 'Objectif secondaire 1'],
      ['objectif_secondaire_2', 'Objectif secondaire 2'],
      ['dureeProbleme', 'Depuis quand'],
      ['objectif_urgency', 'Horizon souhaité'],
      ['motivationProfonde', 'Motivation exprimée'],
      ['dejaEssaye', 'Déjà essayé'],
    ]),
  }),
  Object.freeze({
    title: 'Repères du profil',
    fields: Object.freeze([
      ['age', 'Âge déclaré'],
      ['ageActuel', 'Âge déclaré'],
      ['genre', 'Genre déclaré'],
      ['poids', 'Poids déclaré'],
      ['taille', 'Taille déclarée'],
      ['activitePhysique', 'Activité physique'],
      ['activite', 'Activité physique'],
      ['typeSport', 'Type de sport'],
      ['frequenceSport', 'Fréquence du sport'],
    ]),
  }),
  Object.freeze({
    title: 'Alimentation et digestion',
    fields: Object.freeze([
      ['nbRepas', 'Nombre de repas'],
      ['preferencesAlimentaires', 'Préférences alimentaires'],
      ['restrictionsAlimentaires', 'Restrictions alimentaires'],
      ['regimesSuivis', 'Régimes suivis'],
      ['allergies', 'Allergies déclarées'],
      ['alimentsEvites', 'Aliments évités ou intolérances déclarées'],
      ['alimentsProblematiques', 'Aliments signalés comme problématiques'],
      ['ressentiDigestion', 'Ressenti digestif'],
      ['digestion', 'Digestion'],
      ['transitType', 'Transit déclaré'],
      ['frequenceBallonnements', 'Fréquence des ballonnements'],
      ['ballonnements_post_repas', 'Ballonnements après les repas'],
      ['reflux', 'Reflux déclaré'],
      ['fringalesSucre', 'Fringales sucrées'],
      ['variationsGlycemie', 'Ressenti après les repas'],
      ['hydratation', 'Hydratation déclarée'],
    ]),
  }),
  Object.freeze({
    title: 'Énergie, sommeil et rythme de vie',
    fields: Object.freeze([
      ['energieJournee', 'Énergie dans la journée'],
      ['heuresSommeil', 'Sommeil déclaré'],
      ['reveilsNocturnes', 'Réveils nocturnes'],
      ['difficultesEndormissement', 'Endormissement'],
      ['etatReveil', 'État au réveil'],
      ['niveauStressActuel', 'Stress déclaré'],
      ['tabac', 'Tabac déclaré'],
      ['alcool', 'Alcool déclaré'],
      ['ramadanObserve', 'Ramadan observé'],
    ]),
  }),
  Object.freeze({
    title: 'Informations de santé déclarées',
    fields: Object.freeze([
      ['symptomes', 'Symptômes déclarés'],
      ['pathologies', 'Pathologies déclarées'],
      ['complementsActuels', 'Compléments actuels déclarés'],
      ['antecedentsFamiliaux', 'Antécédents familiaux déclarés'],
      ['contraception', 'Contraception déclarée'],
      ['grossesseActuelle', 'Grossesse déclarée'],
      ['projetGrossesse', 'Projet de grossesse déclaré'],
      ['allaitement', 'Allaitement déclaré'],
    ]),
  }),
]);

function scalarText(value) {
  if (Array.isArray(value)) return value.map(scalarText).filter(Boolean).join(', ');
  if (value === null || value === undefined || typeof value === 'object') return '';
  return [...String(value)]
    .map((character) => {
      const code = character.charCodeAt(0);
      return code <= 31 || code === 127 ? ' ' : character;
    })
    .join('').replace(/\s+/g, ' ').trim();
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const DEFAULT_REDACTED_TEXT_MAX_LENGTH = 800;
const MAX_REDACTED_TEXT_LENGTH = 100_000;

export function redactDirectIdentifiers(value, identity = {}, options = {}) {
  let text = scalarText(value);
  if (!text) return '';
  text = text
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[coordonnée retirée]')
    .replace(/https?:\/\/\S+|www\.\S+/gi, '[lien retiré]')
    .replace(/\b\d{1,2}[./-]\d{1,2}[./-](?:19|20)\d{2}\b/g, '[date retirée]')
    // Mot-clé isolé (jamais dans « identifiés », « avide »…) suivi d'une valeur
    // qui contient un chiffre ; bornes Unicode (\b ignore les accents : il
    // prenait « identifi » dans « identifiés »).
    .replace(/(?<![\p{L}\p{N}])(?:AVS|AHV|NSS|ID)(?![\p{L}\p{N}])\s*[:#-]?\s*(?=[A-Z0-9 .-]*\d)[A-Z0-9][A-Z0-9 .-]{5,}(?![\p{L}\p{N}])/giu,
      '[identifiant retiré]')
    .replace(/(?:\+?\d[\s().-]*){7,15}/g, (match) =>
      `[coordonnée retirée]${/\s$/.test(match) ? ' ' : ''}`)
    .replace(/\b[0-9a-f]{8}-[0-9a-f-]{27,36}\b/gi, '[identifiant retiré]')
    .replace(/\b\d{4,5}\s+[\p{L}][\p{L}' -]{2,}\b/gu, '[lieu retiré]')
    .replace(/\b\d{1,4}\s*,?\s*(?:rue|route|avenue|av\.|chemin|impasse|boulevard|bd\.?|quai)\s+[\p{L}][\p{L}' -]{2,}/giu, '[adresse retirée]')
    .replace(/\b(?:Dr|Dre|Docteur|Madame|Monsieur|Mme|M\.)\s+[\p{Lu}][\p{L}'-]+/gu,
      '[nom de tiers retiré]')
    .replace(/\b(?:mère|père|soeur|sœur|frère|tante|oncle|conjoint(?:e)?)\s+[\p{Lu}][\p{L}'-]+/giu, '[nom de tiers retiré]');
  const names = [identity.prenom, identity.nom, identity.firstName, identity.lastName]
    .map(scalarText).filter((name) => name.length >= 2);
  for (const name of names) {
    text = text.replace(
      new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegExp(name)}(?![\\p{L}\\p{N}])`, 'giu'),
      '[identité retirée]',
    );
  }
  const requestedMaxLength = Number(options?.maxLength);
  const maxLength = Number.isFinite(requestedMaxLength) && requestedMaxLength > 0
    ? Math.min(Math.floor(requestedMaxLength), MAX_REDACTED_TEXT_LENGTH)
    : DEFAULT_REDACTED_TEXT_MAX_LENGTH;
  if (options?.failOnTruncation === true && text.length > maxLength) {
    throw new Error(
      `À valider Anissa — le programme autoritatif dépasse la limite de ${maxLength} caractères après anonymisation. Réduis le document sans couper une phrase avant de préparer le dossier application.`,
    );
  }
  return text.slice(0, maxLength);
}

export function buildExternalProfileSummary(form = {}, identity = {}) {
  const safeForm = form && typeof form === 'object' ? form : {};
  const lines = [
    'SYNTHÈSE FACTUELLE DU PROFIL — SANS IDENTITÉ DIRECTE',
    '',
    'Document de travail. Il reprend uniquement les informations déclarées dans le dossier, sans diagnostic ni interprétation.',
  ];
  let populated = 0;
  const seenKeys = new Set();
  for (const section of PROFILE_SECTIONS) {
    const rows = [];
    for (const [key, label] of section.fields) {
      if (DIRECT_IDENTIFIER_KEYS.has(key) || seenKeys.has(key)) continue;
      const value = redactDirectIdentifiers(safeForm[key], { ...safeForm, ...identity });
      if (!value) continue;
      seenKeys.add(key);
      rows.push(`- ${label} : ${value}`);
      populated += 1;
    }
    if (rows.length > 0) lines.push('', section.title.toUpperCase(), ...rows);
  }
  const treatments = redactDirectIdentifiers(
    formatActiveTreatments(safeForm, { includeMoment: true }),
    { ...safeForm, ...identity },
  );
  if (treatments) {
    lines.push('', 'TRAITEMENTS DÉCLARÉS', `- Traitements en cours : ${treatments}`);
    populated += 1;
  }
  if (populated === 0) {
    lines.push('', 'Aucune information exploitable n’est encore renseignée dans le profil.');
  }
  lines.push('', 'CONSIGNE POUR LE TRAITEMENT EXTERNE');
  lines.push('- Utiliser uniquement les faits ci-dessus et les PDF originaux joints à cette conversation.');
  lines.push('- Les PDF originaux peuvent contenir des identités : ne recopier aucun nom, adresse, lieu précis, téléphone, e-mail, identifiant direct, nom de tiers ou nom de fichier identifiant dans la synthèse finale.');
  lines.push('- Signaler ce qui manque ou se contredit. Ne pas inventer de valeur, de diagnostic ou de lien de causalité.');
  lines.push('- Produire une synthèse d’exploitation structurée, à relire et valider par Anissa avant la génération du plan.');
  return lines.join('\n');
}

export function buildExternalDossierSynthesisPrompt(profileSummary = '') {
  const summary = String(profileSummary ?? '').replace(/\r\n/g, '\n').trim();
  return [
    'PACK DE TRAVAIL — SYNTHÈSE D’EXPLOITATION DU DOSSIER PILOTE',
    '',
    'MISSION',
    'À partir de la synthèse relue ci-dessous et des PDF originaux joints à cette conversation, rédige une synthèse d’exploitation structurée, directement collable dans le champ de synthèse du SaaS Anissa Nutrition.',
    'Cette synthèse servira ensuite de matière au générateur interne du SaaS. Elle ne doit pas contenir le plan alimentaire final.',
    'Le contenu sera relu, corrigé et validé par Anissa avant la génération du plan.',
    '',
    'RÈGLES IMPÉRATIVES',
    '- Utilise uniquement les faits présents dans la synthèse et les PDF originaux joints.',
    '- Les PDF originaux peuvent contenir des identités directes. Ne recopie dans la synthèse finale aucun nom ou prénom, adresse, lieu précis, téléphone, e-mail, identifiant direct, nom de tiers ou nom de fichier identifiant.',
    '- Ne devine aucune information absente et ne transforme jamais une hypothèse en fait.',
    '- Ne pose aucun diagnostic, ne propose aucune modification de traitement et ne donne aucune instruction médicale.',
    '- N’invente ni mesure, ni résultat, ni besoin calculé avec une fausse précision.',
    '- Pour chaque résultat utile, conserve le marqueur ou libellé exact, la valeur exacte, l’unité exacte, la date ou période et le document/page source lorsque ces éléments sont disponibles.',
    '- Conserve le génotype ou libellé d’origine lorsqu’il est pertinent. Ne normalise, ne convertis et ne corrige jamais une valeur ou une unité.',
    '- Présente côte à côte les résultats anciens et récents ou les sources divergentes. Ne choisis jamais automatiquement lequel retenir.',
    '- Si l’unité, la date, la source ou le niveau de certitude manque, conserve le constat disponible et ajoute « À valider Anissa ».',
    '- Signale clairement les informations manquantes, ambiguës ou contradictoires.',
    '- Pour tout point qui demande une décision, une vérification ou une donnée absente, écris exactement : « À valider Anissa : … »',
    '- Conserve les allergies, restrictions, préférences, traitements déclarés et contraintes du dossier comme des faits déclarés, sans les interpréter.',
    '- N’ajoute aucune identité, coordonnée ou information permettant d’identifier directement la cliente.',
    '- Ne produis aucun menu, aucune recette, aucune liste de courses et aucun plan alimentaire final.',
    '- Rédige en français, dans un ton simple, humain et pratique. Évite le jargon et les longues précautions répétitives.',
    '',
    'FORMAT DE SORTIE — CONTRAT STRICT POUR LE CHAMP SYNTHÈSE',
    '- Réponds uniquement en Markdown, sans bloc de code, sans JSON et sans texte avant le premier titre.',
    '- Utilise exactement les titres et sous-titres ci-dessous, dans cet ordre. Ne les renomme pas.',
    '',
    '## SYNTHÈSE D’EXPLOITATION',
    'Un aperçu très court du dossier et de ce qui sera utile à la préparation du plan, sans identité directe.',
    '',
    '## FAITS UTILES DU PROFIL',
    'Reprends uniquement les faits déclarés qui ont un lien utile avec l’accompagnement alimentaire.',
    '',
    '## OBJECTIFS EXPRIMÉS',
    'Distingue clairement l’objectif principal, les objectifs secondaires, les motivations et les priorités exprimées.',
    '',
    '## CONTRAINTES DÉCLARÉES',
    'Regroupe les allergies, restrictions, aliments évités, préférences, traitements déclarés, contraintes de rythme de vie et autres limites explicitement présentes dans le dossier.',
    '',
    '## ALIMENTATION ET HABITUDES',
    'Résume la structure actuelle des repas, les préférences, les difficultés exprimées, l’hydratation, l’activité, le sommeil et le rythme de vie lorsqu’ils sont renseignés.',
    '',
    '## DONNÉES UTILES DES DOCUMENTS',
    'Extrais les constats non chiffrés des PDF qui peuvent éclairer la préparation du plan. Distingue le type de document et la page lorsque cette information est disponible, sans recopier un nom de fichier potentiellement identifiant. Présente chaque donnée comme un constat du document, jamais comme un diagnostic ajouté.',
    '',
    '## DONNÉES CHIFFRÉES UTILES AU PLAN',
    'Présente les résultats utiles sous forme de tableau Markdown ou de liste source-aware. Pour chaque ligne, indique : marqueur ou libellé exact | valeur exacte | unité exacte | date ou période | type de document et page source | remarque ou « À valider Anissa ». Conserve aussi le génotype ou libellé exact lorsqu’il est pertinent. Ne qualifie pas le résultat, ne le convertis pas et ne lui attribue aucun diagnostic. Place les valeurs anciennes/récentes et les sources divergentes côte à côte.',
    '',
    '## CONTRADICTIONS ET INFORMATIONS MANQUANTES',
    'Liste les divergences entre les sources, les données absentes et les éléments illisibles ou incertains. Ne tranche pas à la place d’Anissa.',
    '',
    '## PISTES NUTRITIONNELLES NON DIAGNOSTIQUES',
    'Propose seulement des axes pratiques à envisager lors de la génération du plan, reliés aux faits du dossier. Pas de menu, de quantité, de prescription, de promesse ni de recommandation médicale.',
    '',
    '## POINTS À VALIDER ANISSA',
    'Termine par une liste courte de tous les manques, contradictions, arbitrages et vérifications nécessaires avant la génération du plan. Écris « Aucun point relevé » seulement si les sources permettent réellement de le conclure.',
    '',
    'CONTRÔLE AVANT DE RÉPONDRE',
    '- Vérifie la cohérence entre la synthèse du profil, les documents, les objectifs, les contraintes et les pistes proposées.',
    '- Vérifie que chaque donnée chiffrée conservée possède sa valeur et son unité exactes, et que toute source, date, unité ou incertitude manquante porte « À valider Anissa ».',
    '- Supprime toute affirmation non soutenue par le dossier.',
    '- Vérifie que tu n’as produit ni menu, ni recette, ni liste de courses, ni plan final.',
    '- Vérifie que la réponse finale ne contient aucun nom ou prénom, adresse, lieu précis, téléphone, e-mail, identifiant direct, nom de tiers ou nom de fichier identifiant issu des PDF originaux.',
    '- Vérifie une dernière fois que tous les titres Markdown imposés sont présents exactement une fois.',
    '- Fournis uniquement la synthèse d’exploitation structurée finale, sans expliquer ta méthode et sans l’entourer de ```.',
    '',
    'SYNTHÈSE DU PROFIL RELUE',
    '<<< DÉBUT DE LA SYNTHÈSE SANS IDENTITÉ DIRECTE >>>',
    summary || 'Aucune synthèse renseignée — À valider Anissa.',
    '<<< FIN DE LA SYNTHÈSE SANS IDENTITÉ DIRECTE >>>',
  ].join('\n');
}

export function canExportExternalPlanPrompt({ profileSummary, privacyReviewConfirmed }) {
  return privacyReviewConfirmed === true && String(profileSummary ?? '').trim().length > 0;
}

export async function sha256ExternalText(value) {
  if (!globalThis.crypto?.subtle) throw new Error('Empreinte cryptographique indisponible.');
  const bytes = new TextEncoder().encode(String(value ?? '').replace(/\r\n/g, '\n'));
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function buildExternalPlanProvenance({ importedText, profileSummary,
  privacyReviewConfirmed = false, requestId = globalThis.crypto?.randomUUID?.() }) {
  const normalizedText = String(importedText ?? '').replace(/\r\n/g, '\n').trim();
  const normalizedSummary = String(profileSummary ?? '').replace(/\r\n/g, '\n').trim();
  if (normalizedText.length < 80) {
    throw new Error('Colle une synthèse complète avant de continuer.');
  }
  if (privacyReviewConfirmed !== true || !requestId) {
    throw new Error('Confirme la relecture de la synthèse sans identité directe.');
  }
  return Object.freeze({
    plan_input_mode: EXTERNAL_PLAN_INPUT_MODE,
    review_status: 'a_valider',
    privacy_review_confirmed: true,
    request_id: requestId,
    imported_text_sha256: await sha256ExternalText(normalizedText),
    profile_summary_sha256: await sha256ExternalText(normalizedSummary),
  });
}

export function isExternalPlanInput(value) {
  return value?.plan_input_mode === EXTERNAL_PLAN_INPUT_MODE
    && value?.review_status === 'a_valider'
    && value?.privacy_review_confirmed === true
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value?.request_id || '')
    && /^[a-f0-9]{64}$/.test(value?.imported_text_sha256 || '')
    && /^[a-f0-9]{64}$/.test(value?.profile_summary_sha256 || '')
    && (value?.current_text_sha256 === undefined
      || /^[a-f0-9]{64}$/.test(value.current_text_sha256));
}

export function requiresMgdPlanAdoption(mgdCaseState, externalInput) {
  return mgdCaseState !== 'none' && !isExternalPlanInput(externalInput);
}
